/**
 * ScheduleBuilder — optional trading schedule for strategies and backtests.
 *
 * value: null (disabled) | { days, start_time, end_time, interval_minutes }
 * onChange(value): called with updated schedule or null when disabled
 *
 * Backend DSL format:
 *   { days: ["MON","TUE"], start_time: "09:15", end_time: "15:30", interval_minutes: 60 }
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock } from "lucide-react";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_LABELS = { MON: "Mo", TUE: "Tu", WED: "We", THU: "Th", FRI: "Fr", SAT: "Sa" };

const DEFAULT_SCHEDULE = {
  days: ["MON", "TUE", "WED", "THU", "FRI"],
  start_time: "09:15",
  end_time: "15:30",
  interval_minutes: "",
};

export default function ScheduleBuilder({ value, onChange }) {
  const enabled = value !== null && value !== undefined;
  const schedule = enabled ? value : DEFAULT_SCHEDULE;

  const toggle = () => {
    if (enabled) onChange(null);
    else onChange({ ...DEFAULT_SCHEDULE, interval_minutes: null });
  };

  const update = (patch) => {
    onChange({ ...schedule, ...patch });
  };

  const toggleDay = (day) => {
    const days = schedule.days.includes(day)
      ? schedule.days.filter((d) => d !== day)
      : [...schedule.days, day];
    update({ days });
  };

  return (
    <div className="space-y-3">
      {/* Header toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none ${
            enabled ? "bg-white" : "bg-zinc-700"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-black shadow-lg transform transition-transform ${
              enabled ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
        <span className="overline flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> SCHEDULE
        </span>
        {!enabled && (
          <span className="text-xs txt-muted">Off — runs on every candle</span>
        )}
      </div>

      {enabled && (
        <div className="pl-12 space-y-4">
          {/* Days of week */}
          <div>
            <Label className="overline text-xs mb-2 block">Active days</Label>
            <div className="flex gap-1.5">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`w-9 h-8 text-xs font-section border transition-colors ${
                    schedule.days.includes(d)
                      ? "border-white bg-[var(--bg-surface-2)] text-white"
                      : "border-[var(--border)] txt-muted hover:border-white/40"
                  }`}
                >
                  {DAY_LABELS[d]}
                </button>
              ))}
            </div>
          </div>

          {/* Time window */}
          <div className="grid grid-cols-2 gap-3 max-w-xs">
            <div>
              <Label className="overline text-xs mb-1 block">Start time (IST)</Label>
              <Input
                type="time"
                value={schedule.start_time}
                onChange={(e) => update({ start_time: e.target.value })}
                className="rounded-none h-8 text-sm font-mono-data bg-[var(--bg-page)] border-[var(--border)]"
              />
            </div>
            <div>
              <Label className="overline text-xs mb-1 block">End time (IST)</Label>
              <Input
                type="time"
                value={schedule.end_time}
                onChange={(e) => update({ end_time: e.target.value })}
                className="rounded-none h-8 text-sm font-mono-data bg-[var(--bg-page)] border-[var(--border)]"
              />
            </div>
          </div>

          {/* Interval */}
          <div className="max-w-xs">
            <Label className="overline text-xs mb-1 block">
              Fire interval (minutes) <span className="txt-muted normal-case font-normal">— optional</span>
            </Label>
            <Input
              type="number"
              min={1}
              placeholder="Every candle in window"
              value={schedule.interval_minutes ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                update({ interval_minutes: v === "" ? null : Number(v) });
              }}
              className="rounded-none h-8 text-sm font-mono-data bg-[var(--bg-page)] border-[var(--border)]"
            />
            <p className="text-xs txt-muted mt-1">
              Leave blank to evaluate every candle in the window. Set e.g. 60 to check once per hour.
            </p>
          </div>

          {/* Summary badge */}
          <div className="text-xs font-mono-data txt-muted border border-[var(--border)] px-3 py-2 max-w-sm">
            {schedule.days.length === 0
              ? "⚠ No days selected"
              : `${schedule.days.join(", ")} · ${schedule.start_time}–${schedule.end_time} IST${
                  schedule.interval_minutes ? ` · every ${schedule.interval_minutes}m` : ""
                }`}
          </div>
        </div>
      )}
    </div>
  );
}
